import React from 'react';
import { Star, Trash2, Building2, User as UserIcon, Clock } from 'lucide-react';
import { fmtDate, extractName } from './emailUtils';

// One thread row — memoized so scrolling/typing doesn't re-render 100 rows.
const EmailThreadRow = React.memo(function EmailThreadRow({
  t, selected, checked, showAccount,
  onOpen, onToggleSelect, onToggleStar, onQuickTrash,
}) {
  return (
    <div className={`group relative border-b border-[#F3F4F8] ${selected ? 'bg-[#EEF2FF]' : 'hover:bg-[#FAFAFE]'}`}>
      <div className="absolute left-1 top-3 flex flex-col items-center gap-1.5 z-10">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => { e.stopPropagation(); onToggleSelect(t.thread_id); }}
          onClick={(e) => e.stopPropagation()}
          data-testid={`email-thread-checkbox-${t.thread_id}`}
          className="opacity-40 group-hover:opacity-100"
        />
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onToggleStar(t); }}
          data-testid={`email-thread-star-${t.thread_id}`}
          className={`w-3.5 h-3.5 flex items-center justify-center ${t.starred ? 'text-[#F59E0B] opacity-100' : 'text-[#8E8FA5] opacity-40 group-hover:opacity-100 hover:text-[#F59E0B]'}`}
          title={t.starred ? 'Retirer des suivis' : 'Marquer comme suivi'}
        >
          <Star className={`w-3.5 h-3.5 ${t.starred ? 'fill-current' : ''}`} />
        </button>
      </div>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onQuickTrash(t); }}
        data-testid={`email-thread-trash-${t.thread_id}`}
        className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-opacity z-10 h-6 w-6 rounded text-[#B91C1C] hover:bg-[#FEF2F2] flex items-center justify-center"
        title="Mettre à la corbeille"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>

      <button onClick={() => onOpen(t)} data-testid={`email-thread-${t.thread_id}`} className="w-full text-left pl-9 pr-4 py-3">
        <div className="flex items-baseline justify-between gap-2">
          <span className={`text-[12.5px] truncate ${t.unread ? 'font-bold text-[#1A1B2E]' : 'font-medium text-[#4A4B63]'}`}>
            {extractName(t.from)}
          </span>
          <span className="text-[10.5px] text-[#8E8FA5] flex-shrink-0">{fmtDate(t.date_ms)}</span>
        </div>
        {showAccount && t._account_label && (
          <div className="text-[10px] text-[#4F46E5] truncate mt-0.5" data-testid={`email-thread-account-${t.thread_id}`}>
            📥 {t._account_label}
          </div>
        )}
        <div className={`text-[12.5px] truncate mt-0.5 ${t.unread ? 'font-semibold text-[#1A1B2E]' : 'text-[#6B6C85]'}`}>
          {t.subject}
          {t.messages_count > 1 && <span className="ml-1.5 text-[10px] text-[#8E8FA5]">({t.messages_count})</span>}
        </div>
        <div className="text-[11.5px] text-[#8E8FA5] truncate mt-0.5">{t.snippet}</div>
        {t.crm_match && (
          <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
            <span
              title={`Lié à ${t.crm_match.type === 'entreprise' ? 'entreprise' : t.crm_match.type === 'prospect' ? 'prospect' : 'apprenant'} CRM`}
              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border ${t.crm_match.type === 'entreprise' ? 'bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]' : 'bg-[#EEF2FF] text-[#4F46E5] border-[#C7D2FE]'}`}
              data-testid={`email-thread-crm-${t.thread_id}`}
            >
              {t.crm_match.type === 'entreprise' || t.crm_match.type === 'prospect'
                ? <Building2 className="w-2.5 h-2.5" />
                : <UserIcon className="w-2.5 h-2.5" />}
              {t.crm_match.name}
              {t.crm_match.manual && <span className="opacity-60 ml-0.5">★</span>}
            </span>
          </div>
        )}
        {Array.isArray(t.tags) && t.tags.length > 0 && (
          <div className="mt-1 flex items-center gap-1 flex-wrap" data-testid={`email-thread-tags-${t.thread_id}`}>
            {t.tags.slice(0, 4).map((tag) => (
              <span key={tag} className="inline-flex items-center px-1.5 py-0.5 rounded text-[9.5px] font-semibold bg-[#F3E8FF] text-[#6B21A8] border border-[#D8B4FE]">
                #{tag}
              </span>
            ))}
            {t.tags.length > 4 && <span className="text-[9.5px] text-[#8E8FA5] font-semibold">+{t.tags.length - 4}</span>}
          </div>
        )}
        {t.snoozed_until && (
          <div className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9.5px] font-semibold bg-[#FFF7ED] text-[#C2410C] border border-[#FED7AA]" data-testid={`email-thread-snoozed-${t.thread_id}`}>
            <Clock className="w-2.5 h-2.5" />
            Reporté · {new Date(t.snoozed_until).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })} {new Date(t.snoozed_until).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
          </div>
        )}
        {(t.status && t.status !== 'to_handle') || t.assignee_user_id || (t.comments_count && t.comments_count > 0) ? (
          <div className="mt-1 flex items-center gap-1.5 flex-wrap" data-testid={`email-thread-collab-${t.thread_id}`}>
            {t.status && t.status !== 'to_handle' && (
              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9.5px] font-semibold border ${t.status === 'in_progress' ? 'bg-[#DBEAFE] text-[#1E40AF] border-[#93C5FD]' : t.status === 'done' ? 'bg-[#DCFCE7] text-[#166534] border-[#86EFAC]' : 'bg-[#F3F4F6] text-[#4B5563] border-[#D1D5DB]'}`}>
                {t.status === 'in_progress' ? 'En cours' : t.status === 'done' ? 'Traité' : 'Archivé'}
              </span>
            )}
            {t.assignee_user_id && t.assignee_name && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9.5px] font-semibold bg-[#EEF2FF] text-[#4F46E5] border border-[#C7D2FE]" title={`Assigné à ${t.assignee_name}`}>
                <UserIcon className="w-2.5 h-2.5" /> {t.assignee_name}
              </span>
            )}
            {t.comments_count > 0 && (
              <span className="inline-flex items-center gap-1 text-[9.5px] text-[#6B6C85] font-semibold" title="Commentaires internes">
                💬 {t.comments_count}
              </span>
            )}
          </div>
        ) : null}
      </button>
    </div>
  );
});

export default EmailThreadRow;
